
import React, { useState, useRef } from 'react';
import type { Comment } from '../types/gridTypes';
import { IconTrash, IconReply, IconSend, IconEdit, IconCheck, IconX } from './icons';

interface Props {
  comments: Comment[];
  onAddComment?: (text: string, parentId?: string) => void;
  onEditComment?: (args: { commentId: string; body: string }) => Promise<Comment> | Comment | void;
  onDeleteComment?: (args: { commentId: string }) => Promise<void> | void;
  onResolveComment?: (args: { commentId: string; resolved: boolean }) => Promise<Comment> | Comment | void;
}

export const SidebarComments: React.FC<Props> = ({ 
    comments,
    onAddComment,
    onEditComment,
    onDeleteComment,
    onResolveComment: _onResolveComment
}) => {
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  
  // Edit State
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handlePost = () => {
      if (!newComment.trim()) return;
      onAddComment?.(newComment, replyingTo || undefined);
      setNewComment('');
      setReplyingTo(null);
  };

  const handleReply = (e: React.MouseEvent, commentId: string) => {
      e.stopPropagation();
      setReplyingTo(commentId);
      textareaRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          handlePost();
      }
  };

  const handleDelete = (e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      // For demo smoothness, immediate delete without confirm
      onDeleteComment?.({ commentId: id });
  };

  const startEdit = (e: React.MouseEvent, comment: Comment) => {
      e.stopPropagation();
      setEditingId(comment.id);
      setEditText(comment.body || comment.text || '');
  };

  const saveEdit = (e: React.MouseEvent, commentId: string) => {
      e.stopPropagation();
      if (editText.trim()) {
          onEditComment?.({ commentId, body: editText });
          setEditingId(null);
          setEditText('');
      }
  };

  const cancelEdit = (e: React.MouseEvent) => {
      e.stopPropagation();
      setEditingId(null);
      setEditText('');
  };

  const rootComments = comments.filter(c => !c.parentCommentId);
  
  const CommentItem = ({ comment, isReply = false }: { comment: Comment, isReply?: boolean }) => {
     const isEditing = editingId === comment.id;

     // For demo purposes, allow editing/deleting ALL comments to ensure testing works smoothly
     const canEdit = !!onEditComment;
     const canDelete = !!onDeleteComment;

     return (
        <div className={`relative group ${isReply ? 'mt-3 pl-3 border-l-2 sidebar-reply-border' : ''}`}>
           <div className="flex items-start gap-2.5">
               <div className="sidebar-avatar w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
                  {comment.author ? comment.author.substring(0, 2).toUpperCase() : 'ME'}
               </div>
               <div className="flex-1 min-w-0">
                   <div className="flex items-baseline justify-between">
                       <span className="sidebar-comment-author text-xs font-bold">{comment.author}</span>
                       <span className="sidebar-comment-time text-[10px]">{new Date(comment.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                   </div>

                   {isEditing ? (
                       <div className="mt-2" onClick={e => e.stopPropagation()}>
                           <textarea
                               className="sidebar-edit-textarea w-full text-sm border rounded p-2 outline-none"
                               rows={3}
                               value={editText}
                               onChange={(e) => setEditText(e.target.value)}
                               autoFocus
                           />
                           <div className="flex items-center gap-2 mt-2">
                               <button onClick={(e) => saveEdit(e, comment.id)} className="sidebar-btn-primary flex items-center gap-1 px-2 py-1 rounded text-xs">
                                   <IconCheck className="w-3 h-3" /> Save
                               </button>
                               <button onClick={cancelEdit} className="sidebar-btn-secondary flex items-center gap-1 px-2 py-1 rounded text-xs">
                                   <IconX className="w-3 h-3" /> Cancel
                               </button>
                           </div>
                       </div>
                   ) : (
                       <>
                           <div className="sidebar-comment-body text-sm mt-1 whitespace-pre-wrap leading-relaxed">
                               {comment.body || comment.text}
                           </div>

                           {/* Actions Row - Pill buttons */}
                           <div className="flex items-center gap-2 mt-2 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity">
                              {!isReply && (
                                  <button
                                     onClick={(e) => handleReply(e, comment.id)}
                                     className="sidebar-action-pill flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors"
                                  >
                                     <IconReply className="w-3 h-3" /> Reply
                                  </button>
                              )}

                              {canEdit && (
                                  <button
                                     onClick={(e) => startEdit(e, comment)}
                                     className="sidebar-action-pill flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors"
                                  >
                                     <IconEdit className="w-3 h-3" /> Edit
                                  </button>
                              )}

                              {canDelete && (
                                  <button
                                     onClick={(e) => handleDelete(e, comment.id)}
                                     className="sidebar-action-pill-danger flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors"
                                  >
                                     <IconTrash className="w-3 h-3" /> Delete
                                  </button>
                              )}
                           </div>
                       </>
                   )}
               </div>
           </div>
        </div>
     );
  };

  const ThreadCard = ({ root }: { root: Comment }) => {
      const replies = comments.filter(c => c.parentCommentId === root.id);
      return (
          <div className="sidebar-comment-card p-3 rounded-lg shadow-sm border mb-3">
              <CommentItem comment={root} />
              {replies.length > 0 && (
                  <div className="mt-2 pt-2 border-t sidebar-thread-border">
                      {replies.map(r => (
                          <CommentItem key={r.id} comment={r} isReply />
                      ))}
                  </div>
              )}
          </div>
      );
  };

  const replyingToComment = replyingTo ? comments.find(c => c.id === replyingTo) : null;

  return (
    <div className="sidebar-panel flex flex-col h-full">
      {/* Scrollable Comment List */}
      <div className="flex-1 overflow-y-auto p-4">
          {comments.length === 0 && (
             <div className="sidebar-empty-state flex flex-col items-center justify-center h-40 text-sm italic">
                No comments yet.
             </div>
          )}
          {rootComments.map(c => (
            <ThreadCard key={c.id} root={c} />
          ))}
      </div>

      {/* Fixed Footer Input Area */}
      <div className="sidebar-input-area border-t p-4">
        {replyingToComment && (
            <div className="sidebar-reply-indicator flex items-center justify-between text-xs mb-2 px-3 py-1.5 rounded-md border">
                <span className="font-medium truncate">Replying to {replyingToComment.author}...</span>
                <button onClick={() => setReplyingTo(null)} className="sidebar-reply-close">✕</button>
            </div>
        )}
        <div className="relative group">
            <textarea
                ref={textareaRef}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={replyingTo ? "Write a reply..." : "Write a comment..."}
                className="sidebar-textarea w-full border rounded-lg p-3 pr-10 text-sm outline-none resize-none transition-shadow"
                rows={2}
                style={{ minHeight: '42px', maxHeight: '120px' }}
            />
            <button
                onClick={handlePost}
                disabled={!newComment.trim()}
                className={`sidebar-send-btn absolute bottom-2.5 right-2 p-1.5 rounded-md transition-all ${!newComment.trim() ? 'disabled' : ''}`}
                title="Post Comment"
            >
                <IconSend className="w-4 h-4" />
            </button>
        </div>
        <div className="sidebar-hint text-[10px] mt-2 text-right">
            Press Enter to post
        </div>
      </div>
    </div>
  );
};
